import { Container, Assets, Texture, BlurFilter } from "pixi.js";
import useChapterState from "@/store/modules/useChapterState";

import gsap from "gsap";
import Image from "./Image";
import { canvasSize, getConstants } from "./Config";
import Viewport from "@/store/modules/Viewport";
import Chapter from "./Chapter";

const basePath = (process.env.BASE_URL === './' ? '/' : process.env.BASE_URL);
const sources = [
  basePath + "images/loader/webp/loader-01@2x.webp",
  basePath + "images/loader/webp/loader-02@2x.webp",
  basePath + "images/loader/webp/loader-03@2x.webp",
  basePath + "images/loader/webp/loader-04@2x.webp",
  // "images/loader/webp/loader-05@2x.webp",
  basePath + "images/loader/webp/loader-06@2x.webp",
  basePath + "images/loader/webp/loader-07@2x.webp",
  // "images/loader/webp/loader-08@2x.webp",
  basePath + "images/loader/webp/loader-09@2x.webp",
  basePath + "images/loader/webp/loader-10@2x.webp",
  basePath + "images/loader/webp/loader-11@2x.webp",
  // "images/loader/webp/loader-12@2x.webp",
  // "images/loader/webp/loader-13@2x.webp",
  basePath + "images/loader/webp/loader-14@2x.webp",
  basePath + "images/loader/webp/loader-15@2x.webp",
  // "images/loader/webp/loader-16@2x.webp",
  // "images/loader/webp/loader-17@2x.webp",
  basePath + "images/loader/webp/loader-18@2x.webp",
  basePath + "images/loader/webp/loader-19@2x.webp",
  // "images/loader/webp/loader-20@2x.webp",
  basePath + "images/loader/webp/loader-21@2x.webp",
]

const { state } = useChapterState();

export default class Loader  {
  public container: Container;
  
  private blurContainer: Container;
  private blurFilter: BlurFilter;

  private main: Container;
  private chapters: Chapter[];

  constructor() {
    this.container = new Container();

    this.blurContainer = new Container();
    this.container.addChild(this.blurContainer);

    this.blurFilter = new BlurFilter();
    this.blurFilter.blur = 0;
  }

  async load(main: Container, chapter: Chapter[]) {
    this.main = main;
    this.chapters = chapter;

    // TODO NEED TO LOAD OTHER ASSETS

    let previousPop = Promise.resolve();

    const count = ('VUE_APP_LOADER_COUNT' in process.env) ? Math.min(parseInt(process.env.VUE_APP_LOADER_COUNT), sources.length) : sources.length;
    const skip = process.env.VUE_APP_LOADER_SKIP === 'true';
    // console.log('loader', { count, skip, oui: process.env.VUE_APP_LOADER_COUNT })

    if (skip) {
      state.loadProgress = 100;
      state.showLoadingLight = false;
      state.showLoader = false;
      state.isLoading = false;
      state.showNavigation = true;
      state.allowSwipe = true;
      this.main.alpha = 1;
      state.chapterToLoad = state.activeIndex;
    } else {
      const toLoads = sources.slice(sources.length - count, sources.length);
      for (let i = 0; i < toLoads.length; i++) {
        const image = toLoads[i];
        await Promise.all([previousPop, Assets.load(image)]);
        previousPop = this.popImage(image, i === toLoads.length - 1);
        state.loadProgress = ((i+1) / toLoads.length) * 100;
      }
    }

  }

  getResponsiveFactor() {
    const factor = Viewport.windowWidth / (Viewport.isDesktop ? 1600 : 600)

    const screenRatio = Viewport.windowHeight / Viewport.windowWidth;
    const ratio = Viewport.isDesktop ? 
      ((screenRatio < (915/1600)) ? (screenRatio < (800/1600)) ? 0.667 : 0.833 : 1) :
      (screenRatio < (700/375)) ? (screenRatio < (620/375)) ? 0.667 : 0.833 : 1;

    return factor * ratio;
  }

  popImage(url: string, latest: boolean): Promise<void> {
    if (latest) {
      state.showLoadingLight = false;
    }
    return new Promise((resolve) => {
      const texture: Texture = Assets.get(url);
      const responsiveFactor = this.getResponsiveFactor();
      const adapt = 0.66 * responsiveFactor;

      const imageW = texture.width * adapt;
      const imageH = texture.height * adapt;
      const image = new Image({ url, width: imageW, height: imageH });

      image.pivot.set(texture.width * adapt / 2, texture.height * adapt / 2);
      image.x = canvasSize.width / 2;
      image.y = canvasSize.height / 2;

      if (!latest) {
        const angle = Math.PI * 2 / 24;
        image.rotation = Math.random() * angle - angle / 2;

        this.blurContainer.addChild(image);
      } else {
        this.container.addChild(image);
      }

      const timeline = gsap.timeline();

      timeline.add(resolve, 0.12);

      if (latest) {
        image.alpha = 0;
        this.playIntro(imageW, imageH);
      } else {
        const scale = 1.1;
        timeline.to(image.scale, {
          x: scale,
          y: scale,
          duration: 4,
          ease: 'power1.out'
        }, 0)
      }
    })
  }

  playIntro(latestWidth: number, latestHeight: number) {
    const timeline = gsap.timeline();

    timeline.addLabel('start', 0.1);
    timeline.to(this.blurFilter, {
      blur: 8,
      duration: 1,
      ease: 'quart.out',
      onStart: () => {
        this.blurContainer.filters = [this.blurFilter];
      }
    }, 'start+=0.6')
    timeline.to(this.blurContainer, {
      alpha: 0,
      duration: 1,
      ease: 'power1.out',
    }, 'start+=0.6')

    timeline.set(this.main, {
      alpha: 1,
    }, 0)

    const activeIndex = Chapter.getActiveIndex(state);
    // this.chapters[activeIndex].playIntro();
    const constants = getConstants();

    this.chapters.forEach((chapter) => {
      if (chapter.index === activeIndex) {
        // chapter.playIntro();

        chapter.graphic.scale.set(1)

        const bigWidth = constants.bigWidth;
        const bigHeight = bigWidth / constants.ratio.big;
        const scaleX = latestWidth * 1.33 / constants.bigWidth;
        const scaleY = latestHeight * 1.33 / bigHeight;
        const smallWidth = chapter.graphic.width * scaleX;
        const smallHeight = chapter.graphic.height * scaleY;
        // console.log('scaleX', { graphicW: chapter.graphic.width, latestWidth, scaleX });
        // console.log('chapter.graphic', { graphic: chapter.graphic, scaleX, bigWidth, bigHeight, smallWidth, smallHeight })

        const duration = 0.7;

        timeline.fromTo(
          chapter.containerIntro.scale,
          {
            x: 0.9,
            y: 0.9,
          },
          {
            x: 1,
            y: 1,
            duration: 2,
            ease: 'power1.out',
          },
          0
        )
        timeline.fromTo(
          chapter.containerIntro,
          {
            x: (bigWidth - bigWidth * 0.9) / 2,
            y: (bigHeight - bigHeight * 0.9) / 2,
          },
          {
            x: 0,
            y: 0,
            duration: 2,
            ease: 'power1.out',
          },
          0
        )

        timeline.fromTo(
          chapter.graphic.scale, 
          {
            x: scaleX,
            y: scaleY
          }, {
            x: 1,
            y: 1,
            duration,
            ease: 'quart.inOut',
          },
          'start+=0.5'
        )

        
        timeline.fromTo(
          chapter.image.scale, 
          {
            x: Viewport.isDesktop ? 0.9 : 0.9,
            y: Viewport.isDesktop ? 0.9 : 0.9,
          },
          {
            x: 1,
            y: 1,
            duration,
            ease: 'quart.inOut',
          },
          'start+=0.5'
        )

        
        timeline.add(
          () => {
            state.showLoader = false;
          },
          'start+=0.7'
        )
        timeline.addLabel('end', 'start+=1');
        timeline.add(
          () => {
            state.isLoading = false;
          },
          'end+=0.2'
        )
        timeline.add(
          () => {
            state.allowSwipe = true;
            state.chapterToLoad = state.activeIndex;
          },
          'end+=0.5'
        )
        timeline.add(
          () => {
            state.showNavigation = true;
          },
          'end+=0.7'
        )
        timeline.fromTo(
          chapter.gradient,
          {
            alpha: 0,
          }, {
            alpha: 1,
            duration: 0.5,
            ease: 'quart.out',
          },
          'start+=0.5'
        )

      } else if (chapter.index === activeIndex - 1) {
        (window as any).debug = chapter;
        timeline.fromTo(
          chapter.containerIntro,
          {
            x: -constants.offset
          },
          {
            x: 0,
            duration: 0.7,
            ease: 'quart.out',
          },
          'start+=1'
        )
      } else if (chapter.index === activeIndex + 1) {
        timeline.fromTo(
          chapter.containerIntro,
          {
            x: constants.offset
          },
          {
            x: 0,
            duration: 0.7,
            ease: 'quart.out',
          },
          'start+=1'
        )
      }
    })
  }
}